import { USER_ROLES } from '@/constants/constants';

/**
 * @typedef {Object} ColumnConfig
 * @property {string} field - The key in the data object
 * @property {string} label - The header label
 * @property {string} [linkField] - Optional field to use for generating a link (e.g., 'id')
 * @property {function} [render] - Optional custom render function: (value, row) => ReactNode
 * @property {boolean} [renderSessionLinks] - Special flag for rendering session lists
 */

/**
 * @typedef {Object} EditableFieldConfig
 * @property {string} name - The field name in the form data
 * @property {string} label - The label for the input
 * @property {'text'|'checkbox'|'select'} type - Input type
 * @property {string|number} [width] - Width of the input
 * @property {number} [flex] - Flex grow value
 * @property {string} [placeholder] - Placeholder text
 * @property {boolean} [multiline] - Multiline text input
 * @property {number} [minRows] - Min rows for multiline
 * @property {number} [maxRows] - Max rows for multiline
 * @property {string[]} [options] - Options for select type
 */

/**
 * @typedef {Object} AssociationConfig
 * @property {string} name - Display name of the association
 * @property {string} key - Unique key for the association
 * @property {string} [dataField] - Field in parent data containing the records (for nested data)
 * @property {string} [idField] - Unique ID field of the child record (default: 'id')
 * @property {boolean} [disableAdd] - Hide the "Add" button
 * @property {ColumnConfig[]} columns - Columns to display in table view
 * @property {string} [detailRoute] - Route pattern for row clicks
 * @property {function} [getRowKey] - Function to generate unique key for table rows
 * @property {function} [transformData] - Transform raw data before display
 * @property {function} [getPreloadedData] - Extract data from parent object manually
 * @property {function} [getFetchEndpoint] - Async string provider for fetching data
 * @property {function} [filterFn] - Filter records after fetching
 * @property {string} [requiredRole] - Minimum role required to view
 * 
 * -- Editable Props --
 * @property {boolean} [isEditable] - Allow editing
 * @property {EditableFieldConfig[]} [editableFields] - Fields for the edit form
 * @property {string} [availableRecordsEndpoint] - Endpoint to fetch options
 * @property {function} [getOptionLabel] - Label formatter for dropdown
 * @property {boolean} [onlyAllowUnique] - Prevent duplicate assignments
 * @property {string} [emptyStateText] - Text when no records found
 * @property {string} [addButtonText] - Text for add button
 */

export const ASSOCIATION_TO_TABLE_TYPE = {
    job_analysts: 'analysts',
    clients: 'clients',
    sessions: 'sessions',
    finances: 'finances',
    client_contacts: 'client_contacts',
    vendors: 'vendors',
    host_recruiter: 'vendors',
    facilities: 'facilities',
    jobs: 'jobs',
};

export const ASSOCIATION_CONFIGS = {
    // ========================================
    // JOB ASSOCIATIONS
    // ========================================
    job: {
        job_analysts: {
            name: 'Job Analysts',
            key: 'job_analysts',
            dataField: 'analysts',
            idField: 'id',
            disableAdd: true,
            columns: [
                { field: 'name', label: 'Name', linkField: 'id' },
                { field: 'email', label: 'Email' },
                { field: 'lead', label: 'Lead', render: (v) => v === true ? 'Yes' : v === false ? 'No' : '—' },
                { field: 'notes', label: 'Notes' },
            ],
            getRowKey: (r, i) => r.id || `analyst-${i}`,
            detailRoute: '/projects/:projectId/hart/analysts/:id',
            // Editable configuration
            isEditable: true,
            editableFields: [
                {
                    name: 'lead',
                    label: 'Lead',
                    type: 'checkbox',
                    width: 50,
                },
                {
                    name: 'notes',
                    label: 'Notes',
                    type: 'text',
                    flex: 1,
                    multiline: true,
                    minRows: 1,
                    maxRows: 3,
                    placeholder: 'Add notes...',
                },
            ],
            availableRecordsEndpoint: '/hart/analysts?dropdown=true&show_all_jobs=true',
            getOptionLabel: (option) => option ? `${option.name} (${option.email})` : '',
            onlyAllowUnique: true,
            emptyStateText: 'No analysts assigned to this job.',
            addButtonText: 'Assign Analyst',
        },
        clients: {
            name: 'Clients',
            key: 'clients',
            dataField: 'clients', // Use clients array from job response (many-to-many)
            idField: 'org_id',
            columns: [
                { field: 'name', label: 'Client Name', linkField: 'org_id' },
                { field: 'org_website', label: 'Website' },
                { field: 'org_phone', label: 'Phone' },
                { field: 'sector', label: 'Sector' },
                { field: 'inactive', label: 'Status', render: (v) => v ? 'Inactive' : 'Active' },
            ],
            getRowKey: (r, i) => r.org_id || r.id || `client-${i}`,
            detailRoute: '/projects/:projectId/hart/clients/:org_id',
            // Editable configuration
            isEditable: true,
            editableFields: [],
            availableRecordsEndpoint: '/hart/clients',
            getOptionLabel: (option) => option ? `${option.name} (${option.org_id})` : '',
            onlyAllowUnique: true,
            emptyStateText: 'No clients assigned to this job.',
            addButtonText: 'Assign Client',
        },
        sessions: {
            name: 'Sessions',
            key: 'sessions',
            dataField: 'sessions',
            idField: 'session_id',
            columns: [
                { field: 'session_id', label: 'Session ID', linkField: 'session_id' },
                { field: 'sess_type', label: 'Session Type' },
                { field: 'sess_start', label: 'Session Start' },
                { field: 'sample_geography', label: 'Sample Geography' },
                { field: 'sample_type', label: 'Sample Type' },
                { field: 'sample_n', label: 'Sample N' },
                { field: 'vendor_name', label: 'Vendor' },
            ],
            getRowKey: (r, i) => r.session_id || `session-${i}`,
            detailRoute: '/projects/:projectId/hart/sessions/:session_id',
            // Editable configuration
            isEditable: true,
            editableFields: [],
            availableRecordsEndpoint: '/hart/sessions',
            getOptionLabel: (option) => option ? `${option.session_id}${option.sess_type ? ` - ${option.sess_type}` : ''}` : '',
            onlyAllowUnique: true,
            emptyStateText: 'No sessions assigned to this job.',
            addButtonText: 'Assign Session',
        },
        finances: {
            name: 'Finances',
            key: 'finances',
            idField: 'Email',
            requiredRole: USER_ROLES.CONTRIBUTOR.level,
            getFetchEndpoint: async (parentId, parentData) => {
                // Placeholder: endpoint doesn't exist yet
                // Will return null to show empty state
                return null;
            },
            columns: [
                { field: 'Email', label: 'Email', linkField: 'Email' },
                { field: 'Type', label: 'Type' },
                { field: 'Percentage', label: 'Percentage' },
            ],
            getRowKey: (r, i) => r.Email || `finance-${i}`,
            detailRoute: '/projects/:projectId/hart/finances/:Email',
            // Editable configuration
            isEditable: true,
            editableFields: [
                {
                    name: 'percentage',
                    label: 'Percentage',
                    type: 'text',
                    width: 120,
                    placeholder: 'Enter %',
                },
                {
                    name: 'notes',
                    label: 'Notes',
                    type: 'text',
                    flex: 1,
                    multiline: true,
                    minRows: 1,
                    maxRows: 1,
                    placeholder: 'Add notes...',
                },
            ],
            onlyAllowUnique: true,
            emptyStateText: 'No finances assigned to this job.',
            addButtonText: 'Assign Finance',
        },
    },

    // ========================================
    // SESSION ASSOCIATIONS
    // ========================================
    session: {
        vendors: {
            name: 'Quantitative Vendors',
            key: 'vendors',
            dataField: 'vendor',
            idField: 'vendor_id',
            // Transform single vendor object to array (if exists)
            transformData: (vendor) => {
                if (!vendor || (vendor.type === 'Host' || vendor.type === 'Recruiter')) return [];
                return [vendor];
            },
            columns: [
                { field: 'name', label: 'Vendor Name', linkField: 'vendor_id' },
                { field: 'type', label: 'Type' },
                { field: 'telephone', label: 'Phone' },
                { field: 'email', label: 'Email' },
            ],
            getRowKey: (r, i) => r.vendor_id || `vendor-${i}`,
            detailRoute: '/projects/:projectId/hart/vendors/:vendor_id',
            // Editable configuration
            isEditable: true,
            editableFields: [],
            availableRecordsEndpoint: '/hart/vendors?exclude_vendor_types=Host,Recruiter',
            getOptionLabel: (option) => option ? `${option.name} (${option.vendor_id})` : '',
            onlyAllowUnique: true,
            emptyStateText: 'No vendor assigned to this session.',
            addButtonText: 'Assign Vendor',
        },
        host_recruiter: {
            name: 'Qualitative Vendors',
            key: 'host_recruiter',
            idField: 'vendor_id',
            // Combine hosts and recruiters from pre-loaded data, adding type field
            getPreloadedData: (parentData) => {
                const hosts = (parentData?.hosts || []).map(h => ({ ...h, type: 'Host' }));
                const recruiters = (parentData?.recruiters || []).map(r => ({ ...r, type: 'Recruiter' }));
                return [...hosts, ...recruiters];
            },
            columns: [
                { field: 'name', label: 'Vendor Name', linkField: 'vendor_id' },
                { field: 'type', label: 'Role' },
                { field: 'telephone', label: 'Phone' },
                { field: 'email', label: 'Email' },
                { field: 'notes', label: 'Notes' },
            ],
            getRowKey: (r, i) => r.host_details_id || r.recruiter_details_id || `host-recruiter-${i}`,
            detailRoute: '/projects/:projectId/hart/vendors/:vendor_id',
            // Editable configuration
            isEditable: true,
            editableFields: [
                {
                    name: 'notes',
                    label: 'Notes',
                    type: 'text',
                    flex: 1,
                    multiline: true,
                    minRows: 1,
                    maxRows: 3,
                    placeholder: 'Add notes...',
                },
            ],
            availableRecordsEndpoint: '/hart/vendors?vendor_types=Host,Recruiter',
            getOptionLabel: (option) => option ? `${option.name} (${option.vendor_id})` : '',
            onlyAllowUnique: true,
            emptyStateText: 'No hosts or recruiters assigned to this session.',
            addButtonText: 'Assign Host/Recruiter',
        },
        facilities: {
            name: 'In Person Qualitative',
            key: 'facilities',
            idField: 'facility_id',
            getFetchEndpoint: async (parentId, parentData) => {
                if (!parentData?.facility_id) return null;
                return `/hart/facilities/${parentData.facility_id}`;
            },
            columns: [
                { field: 'name', label: 'Facility Name', linkField: 'facility_id' },
                { field: 'telephone', label: 'Phone' },
                { field: 'email', label: 'Email' },
                { field: 'city', label: 'City' },
                { field: 'state', label: 'State' },
            ],
            getRowKey: (r, i) => r.facility_id || `facility-${i}`,
            detailRoute: '/projects/:projectId/hart/facilities/:facility_id',
            // Editable configuration
            isEditable: true,
            editableFields: [],
            availableRecordsEndpoint: '/hart/facilities',
            getOptionLabel: (option) => option ? `${option.name} - ${option.city || ''}, ${option.state || ''}` : '',
            onlyAllowUnique: true,
            emptyStateText: 'No facility assigned to this session.',
            addButtonText: 'Assign Facility',
        },
    },

    // ========================================
    // CLIENT ASSOCIATIONS
    // ========================================
    client: {
        client_contacts: {
            name: 'Client Contacts',
            key: 'client_contacts',
            dataField: 'client_contacts',
            idField: 'id',
            columns: [
                { field: 'contact_name', label: 'Name', linkField: 'id' },
                { field: 'contact_title', label: 'Title' },
                { field: 'contact_email', label: 'Email' },
                { field: 'inactive', label: 'Status', render: (v) => v ? 'Inactive' : 'Active' },
            ],
            getRowKey: (r, i) => r.id || `contact-${i}`,
            detailRoute: '/projects/:projectId/hart/client_contacts/:id',
            // Editable configuration
            isEditable: true,
            editableFields: [],
            availableRecordsEndpoint: '/hart/client_contacts',
            getOptionLabel: (option) => option ? `${option.contact_name || ''} (${option.contact_email || ''})` : '',
            onlyAllowUnique: true,
            emptyStateText: 'No contacts assigned to this client.',
            addButtonText: 'Assign Contact',
        },
        jobs: {
            name: 'Active Jobs',
            key: 'jobs',
            dataField: 'jobs',
            idField: 'job_no',
            columns: [
                { field: 'job_no', label: 'Job No', linkField: 'job_no' },
                { field: 'job_name', label: 'Job Name' },
                { field: 'sessions', label: 'Sessions', renderSessionLinks: true },
            ],
            getRowKey: (r, i) => r.job_no || `job-${i}`,
            detailRoute: '/projects/:projectId/hart/jobs/:job_no',
            // Editable configuration
            isEditable: true,
            editableFields: [],
            availableRecordsEndpoint: '/hart/jobs?show_all=false&all=true&dropdown=true',
            getOptionLabel: (option) => option ? `${option.job_no} - ${option.job_name || ''}` : '',
            onlyAllowUnique: true,
            emptyStateText: 'No jobs assigned to this client.',
            addButtonText: 'Assign Job',
        },
    },

    // ========================================
    // VENDOR ASSOCIATIONS
    // ========================================
    vendor: {
        sessions: {
            name: 'Active Jobs',
            key: 'jobs',
            idField: 'job_no',
            dataField: 'sessions',
            columns: [
                { field: 'job_no', label: 'Job No', linkField: 'job_no' },
                { field: 'job_name', label: 'Job Name' },
                { field: 'sessions', label: 'Sessions', renderSessionLinks: true },
            ],
            getRowKey: (r, i) => r.job_no || `job-${i}`,
            detailRoute: '/projects/:projectId/hart/jobs/:job_no',
            // Editable configuration
            // isEditable: true,
            editableFields: [],
            availableRecordsEndpoint: '/hart/jobs?show_all=false',
            getOptionLabel: (option) => option ? `${option.job_no} - ${option.job_name || ''}` : '',
            onlyAllowUnique: true,
            emptyStateText: 'No jobs assigned to this vendor.',
            addButtonText: 'Assign Job',
        },
    },

    // ========================================
    // FACILITY ASSOCIATIONS
    // ========================================
    facility: {
        sessions: {
            name: 'Active Jobs',
            key: 'sessions',
            idField: 'job_no',
            dataField: 'sessions',
            columns: [
                { field: 'job_no', label: 'Job No', linkField: 'job_no' },
                { field: 'job_name', label: 'Job Name' },
                { field: 'sessions', label: 'Sessions', renderSessionLinks: true },
            ],
            getRowKey: (r, i) => r.job_no || `job-${i}`,
            detailRoute: '/projects/:projectId/hart/jobs/:job_no',
            // Editable configuration
            // isEditable: true,
            editableFields: [],
            availableRecordsEndpoint: '/hart/jobs?show_all=false&all=true',
            getOptionLabel: (option) => option ? `${option.job_no} - ${option.job_name || ''}` : '',
            onlyAllowUnique: true,
            emptyStateText: 'No jobs assigned to this facility.',
            addButtonText: 'Assign Job',
        },
    },

    // ========================================
    // ANALYST ASSOCIATIONS
    // ========================================
    analyst: {
        jobs: {
            name: 'Active Jobs',
            key: 'jobs',
            dataField: 'jobs',
            idField: 'job_no',
            columns: [
                { field: 'job_no', label: 'Job No', linkField: 'job_no' },
                { field: 'job_name', label: 'Job Name' },
                { field: 'lead', label: 'Lead', render: (v) => v ? 'Yes' : 'No' },
                { field: 'notes', label: 'Notes' },
            ],
            getRowKey: (r, i) => r.job_no || `job-${i}`,
            detailRoute: '/projects/:projectId/hart/jobs/:job_no',
            isEditable: true,
            editableFields: [
                {
                    name: 'lead',
                    label: 'Lead',
                    type: 'checkbox',
                    width: 50,
                },
                {
                    name: 'notes',
                    label: 'Notes',
                    type: 'text',
                    flex: 1,
                    multiline: true,
                    minRows: 1,
                    maxRows: 3,
                    placeholder: 'Add notes...',
                },
            ],
            availableRecordsEndpoint: '/hart/jobs?show_all=false&all=true',
            getOptionLabel: (option) => option ? `${option.job_no} - ${option.job_name}` : '',
            onlyAllowUnique: true,
            emptyStateText: 'No jobs assigned to this analyst.',
            addButtonText: 'Assign Job',
        },
    },
};
